import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { namespace } from '@common-config/constants';
import { backendApi, type MyBridgeVoiceOption } from '@common/reducers/backend-api/backend-api';
import { Loader2, Play, Square } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT = 'default';

/**
 * The voice an agent speaks with — its voice notes AND its calls (policy `voiceName`). The list is the
 * voice provider's (GET my-bridge/voices — Google today), grouped female / male; Listen plays a short
 * sample in the UI language. '' = the provider's default voice.
 */
export function VoicePicker({
  value,
  onChange,
  dir,
  classes,
}: {
  value: string;
  onChange: (voice: string) => void;
  dir?: 'ltr' | 'rtl';
  classes: { label: string; hint: string; input: string };
}) {
  const { t, i18n } = useTranslation(namespace);
  const { data: catalog } = backendApi.useGetMyBridgeVoicesQuery();
  const [preview, { isLoading }] = backendApi.usePreviewMyBridgeVoiceMutation();
  const [playing, setPlaying] = useState(false);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => audio.current?.pause(), []);

  const selected = value || catalog?.defaultVoice || '';
  const nameOf = (v: MyBridgeVoiceOption) => `${v.id} · ${t(`myBridge.voice.styles.${v.style}`, v.style)}`;
  const group = (gender: 'female' | 'male') => (catalog?.voices ?? []).filter((v) => v.gender === gender);

  const listen = async () => {
    if (playing) {
      audio.current?.pause();
      setPlaying(false);
      return;
    }
    if (!selected) return;
    try {
      const r = await preview({ voice: selected, lang: i18n.language || 'en' }).unwrap();
      audio.current?.pause();
      const a = new Audio(`data:${r.mime_type};base64,${r.data_base64}`);
      audio.current = a;
      a.onended = () => setPlaying(false);
      setPlaying(true);
      await a.play();
    } catch {
      setPlaying(false);
    }
  };

  return (
    <div className="space-y-1" data-testid="agent-voice-picker">
      <Label className={classes.label}>{t('myBridge.voice.label')}</Label>
      <div className="flex items-center gap-2">
        <Select dir={dir} value={value || DEFAULT} onValueChange={(v) => onChange(v === DEFAULT ? '' : v)}>
          <SelectTrigger className={classes.input} data-testid="agent-voice-select"><SelectValue /></SelectTrigger>
          <SelectContent zIndex={30000} className="min-w-[var(--radix-select-trigger-width)]">
            <SelectItem value={DEFAULT}>{t('myBridge.voice.default', { voice: catalog?.defaultVoice ?? '' })}</SelectItem>
            {(['female', 'male'] as const).map((g) => (
              <SelectGroup key={g}>
                <SelectLabel>{t(`myBridge.voice.${g}`)}</SelectLabel>
                {group(g).map((v) => <SelectItem key={v.id} value={v.id}>{nameOf(v)}</SelectItem>)}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" onClick={listen} disabled={!selected || isLoading} data-testid="agent-voice-listen">
          {isLoading ? <Loader2 className="animate-spin" /> : playing ? <Square /> : <Play />}
          {playing ? t('myBridge.voice.stop') : t('myBridge.voice.listen')}
        </Button>
      </div>
      <p className={classes.hint}>{t('myBridge.voice.hint')}</p>
    </div>
  );
}
